import {Component, OnInit} from "@angular/core";
import {Router, ActivatedRoute} from "@angular/router";
import {FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors} from "@angular/forms";
import {switchMap} from "rxjs/operators";
import {WebService} from "../../services/web/web.service";
import {SpinnerService} from "../../services/spinner/spinner.service";
import {ToastService} from "../../services/notification/toast.service";
import {RbacService} from "../../onboarding-users-roles/rbac.service";
import {SecurityService} from "../../services/security.service";

interface RoleOption {
    id: number | string;
    name: string;
}

@Component({
    selector: "app-onboarding-user-create",
    templateUrl: "./onboarding-user-create.component.html",
    styleUrls: ["./onboarding-user-create.component.scss"],
    standalone: false
})
export class OnboardingUserCreateComponent implements OnInit {
    private readonly maxUserFieldLength = 120;
    userForm: FormGroup;
    hidePassword = true;
    roles: RoleOption[] = [];

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private webService: WebService,
        private rbacService: RbacService,
        private spinner: SpinnerService,
        private toast: ToastService,
        private securityService: SecurityService
    ) {
        this.userForm = this.formBuilder.group({
            name_user: ["", [Validators.required, Validators.minLength(2), Validators.maxLength(this.maxUserFieldLength)]],
            lastName_user: ["", [Validators.required, Validators.minLength(2), Validators.maxLength(this.maxUserFieldLength)]],
            email_user: ["", [Validators.required, Validators.email, Validators.maxLength(this.maxUserFieldLength)]],
            password: ["", [Validators.required, this.passwordValidator]],
            roleId: ["", [Validators.required]],
        });
    }

    ngOnInit(): void {
        console.log("📝 Inicializando componente de creación de usuario");
        this.loadRoles();
    }

    save() {
        if (this.userForm.invalid) {
            this.toast.showWarning("Por favor complete todos los campos requeridos correctamente");
            return;
        }

        console.log("💾 Guardando nuevo usuario");
        this.spinner.open();

        const partnerId = this.securityService.getTokenPartner();
        if (!partnerId) {
            console.warn("⚠️ No se encontró partner ID en sesión");
            this.spinner.close();
            return this.toast.showInfo("No se puede crear usuario porque no se encontró un partner.");
        }

        const body = {
            partnerId: partnerId,
            name_user: this.userForm.value.name_user,
            lastName_user: this.userForm.value.lastName_user,
            email_user: this.userForm.value.email_user,
            password: this.userForm.value.password,
        };

        this.webService
            .post(body, `${this.webService.HOST}/api/users/onboarding-admin`)
            .pipe(switchMap((response) => {
                const userId = this.extractCreatedUserId(response);
                if (!userId) {
                    throw new Error("Usuario creado, pero no se pudo identificar para asignar el rol.");
                }
                return this.rbacService.addRoleToUser(Number(partnerId), userId, this.userForm.value.roleId);
            }))
            .subscribe(
                () => {
                    console.log("✅ Usuario creado exitosamente");
                    this.spinner.close();
                    this.toast.showSuccess("Usuario creado exitosamente");
                    this.goBack();
                },
                (error) => {
                    console.error("❌ Error al crear usuario:", error.message || error);
                    this.spinner.close();
                    this.toast.showError(error);
                }
            );
    }

    loadRoles() {
        const partnerId = this.securityService.getTokenPartner();
        if (!partnerId) {
            this.roles = [];
            return;
        }

        this.rbacService.listRoles(Number(partnerId)).subscribe(
            (response) => {
                this.roles = this.extractRoles(response)
                    .map((role: any) => ({
                        id: role.id || role.roleId || role.role_id,
                        name: role.name || role.roleName || role.nombre,
                    }))
                    .filter((role: RoleOption) => role.id && role.name);
            },
            (error) => {
                this.roles = [];
                this.toast.showError(error);
            }
        );
    }

    private extractCreatedUserId(response: any): number | string | null {
        return response?.id ||
            response?.userId ||
            response?.result?.id ||
            response?.result?.userId ||
            response?.data?.id ||
            response?.data?.userId ||
            response?.result?.data?.id ||
            response?.result?.data?.userId ||
            null;
    }

    private extractRoles(response: any): any[] {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.data?.roles)) return response.data.roles;
        if (Array.isArray(response?.result?.data?.roles)) return response.result.data.roles;
        if (Array.isArray(response?.result)) return response.result;
        if (Array.isArray(response?.roles)) return response.roles;
        return [];
    }

    goBack() {
        this.router.navigate(["../"], {
            relativeTo: this.activatedRoute,
            queryParamsHandling: "preserve",
        });
    }

    /**
     * Validador personalizado para contraseñas seguras
     * Requisitos:
     * - Mínimo 12 caracteres
     * - Al menos 1 mayúscula (A-Z)
     * - Al menos 1 minúscula (a-z)
     * - Al menos 1 número (0-9)
     * - Al menos 1 carácter especial (!@#$%^&*()-_=+[]{};:,.<>?/|)
     */
    passwordValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;

        if (!value) {
            return null;
        }

        const hasMinLength = value.length >= 12;
        const hasUpperCase = /[A-Z]/.test(value);
        const hasLowerCase = /[a-z]/.test(value);
        const hasNumber = /[0-9]/.test(value);
        const hasSpecialChar = /[!@#$%^&*()\\-_=+\\\[\\\]{};:,.<>?/|\\\\'""]/.test(value);

        const passwordValid = hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;

        if (!passwordValid) {
            return {
                passwordStrength: {
                    hasMinLength,
                    hasUpperCase,
                    hasLowerCase,
                    hasNumber,
                    hasSpecialChar
                }
            };
        }

        return null;
    }

    /**
     * Genera una contraseña aleatoria que cumple con todos los requisitos de seguridad
     */
    generatePassword(): void {
        const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
        const numberChars = '0123456789';
        const specialChars = '!@#$%^&*()-_=+[]{};:,.<>?/|';

        // Asegurar que tenga al menos uno de cada tipo requerido
        let password = '';
        password += uppercaseChars[Math.floor(Math.random() * uppercaseChars.length)];
        password += lowercaseChars[Math.floor(Math.random() * lowercaseChars.length)];
        password += numberChars[Math.floor(Math.random() * numberChars.length)];
        password += specialChars[Math.floor(Math.random() * specialChars.length)];

        // Completar hasta 12 caracteres con caracteres aleatorios de todos los tipos
        const allChars = uppercaseChars + lowercaseChars + numberChars + specialChars;
        for (let i = password.length; i < 12; i++) {
            password += allChars[Math.floor(Math.random() * allChars.length)];
        }

        // Mezclar los caracteres para que no sigan un patrón predecible
        password = password.split('').sort(() => Math.random() - 0.5).join('');

        // Establecer la contraseña en el formulario
        this.userForm.patchValue({password});
        this.toast.showSuccess('Contraseña generada exitosamente');
    }

    /**
     * Obtiene los errores de validación del password para mostrar en el template
     */
    getPasswordErrors(): any {
        const passwordControl = this.userForm.get('password');
        if (passwordControl?.hasError('passwordStrength')) {
            return passwordControl.errors?.['passwordStrength'];
        }
        return null;
    }
}
